import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Button } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Swal from "sweetalert2";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Error from "../../components/Error/Error";
import Information from "../Information/Information";

import useLocalStorage from "../../services/hooks/useLocalStorage";
import Op from "../../services/api/op.service";

import Modal from "../../components/Modal/Modal";
import SampleBillNo from "../../assets/images/sample-billing-number.png";

import "./Landing.css";
import verifyUserIdService from "../../services/api/verifyUserId.service";
import {
  clearAccessToken,
  getAccessToken,
  setAccessToken,
} from "../../services/api/browser-session";

const iconStyle = {
  cursor: "pointer",
  color: "#4ab7ff",
  "&:hover": {
    color: "#007bff",
  },
};

// landing page
function Landing() {
  const billNoRef = useRef();
  const autoRedirectedBillingNoRef = useRef();

  // qcuserid parameter from url is used for display only
  // Actual authorization must be validated server-side using session/token
  // adding another handling to prevent other users from entering other billing numbers
  const { qcuserid } = useParams();

  const location = useLocation();

  // parameters:
  // - billingNumber can be passed as a parameter to automatically fill the billing number field
  // - backButton can be passed as a parameter to hide(0)/show(1) the back button default is to show back button
  const { billingNumber, backButton = "1" } = Object.fromEntries(
    new URLSearchParams(location.search),
  );

  const [, setStoreBillNo] = useLocalStorage("billNo", "");
  const [modal, setModal] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setIsLoading(true);
    setIsVerified(false);
    const verify = async () => {
      try {
        const result = await verifyUserIdService.fetchUserId(qcuserid, {
          signal: controller.signal,
        });
        if (!active) return;
        if (result.data?.status !== "success") {
          clearAccessToken();
          return;
        }
        if (result.data.token !== undefined) {
          setAccessToken(result.data.token);
          // Keep the bearer in the fragment, never in an HTTP query string.
          // The session consumer removes it on the next protected request.
          const fragment = new URLSearchParams(window.location.hash.slice(1));
          fragment.set("payment_session", result.data.token);
          window.history.replaceState(
            window.history.state,
            "",
            `${window.location.pathname}${
              window.location.search
            }#${fragment.toString()}`,
          );
          setIsVerified(true);
        } else {
          // Existing bill-scoped links can verify without receiving a new grant.
          setIsVerified(Boolean(getAccessToken()));
        }
      } catch (error) {
        if (active) {
          clearAccessToken();
          setIsVerified(false);
        }
      } finally {
        if (active) setIsLoading(false);
      }
    };
    verify();
    return () => {
      active = false;
      controller.abort();
    };
  }, [qcuserid]);

  const fetchBillingNumber = async (billNo) => {
    const res = await Op.fetchOpList(billNo);
    const responseUserId = String(res.data.data.user_id);
    const urlUserId = String(qcuserid);

    console.log("URL User ID:", urlUserId); // eslint-disable-line
    console.log("Response User ID:", responseUserId); // eslint-disable-line

    if (urlUserId !== responseUserId) {
      throw new Error("Wrong billing number.");
    }

    setStoreBillNo(billNo);
    return res.data.data;
  };

  const showBillingNumberError = (err) => {
    if (
      err?.response?.status === 403 &&
      err?.response?.data?.message === "Access denied for this bill"
    ) {
      Swal.fire({
        title: "Bill unavailable",
        icon: "warning",
        text: "This bill is unavailable or not linked to this user. Check the billing number on your order of payment and try again.",
        allowOutsideClick: false,
        scrollbarPadding: false,
      });
    } else if (
      err?.code === "PAYMENT_SESSION_REQUIRED" ||
      [401, 403].includes(err?.response?.status)
    ) {
      Swal.fire({
        title: "Payment access required",
        icon: "warning",
        text: "To view this bill, open a fresh payment link from the application that issued your order of payment.",
        allowOutsideClick: false,
        scrollbarPadding: false,
      });
    } else if (err?.response?.data?.message === "Billing number not found.") {
      Swal.fire({
        title: "Billing number not found.",
        icon: "warning",
        text: "Please make sure that you have typed the correct billing number based on your order of payment.",
        allowOutsideClick: false,
        scrollbarPadding: false,
      });
    } else if (err?.response?.data?.message === "Billing number required.") {
      Swal.fire({
        title: "Field is empty.",
        icon: "warning",
        text: "Please make sure that you type the correct billing number based on your order of payment.",
        allowOutsideClick: false,
        scrollbarPadding: false,
      });
    } else if (err?.toString().includes("Wrong billing number.")) {
      Swal.fire({
        title: "Invalid billing number",
        icon: "warning",
        text: "Please make sure that you type the correct billing number of your application.",
        allowOutsideClick: false,
        scrollbarPadding: false,
      });
    } else {
      Swal.fire({
        title: "Error",
        icon: "error",
        text: "Something went wrong. Please try again later.",
        allowOutsideClick: false,
        scrollbarPadding: false,
      });
    }
  };

  useEffect(() => {
    if (isLoading || !isVerified || !billingNumber) {
      return;
    }

    const requestKey = `${qcuserid}:${billingNumber}`;
    if (autoRedirectedBillingNoRef.current === requestKey) {
      return;
    }
    autoRedirectedBillingNoRef.current = requestKey;

    const showBillingInformation = async () => {
      try {
        const userData = await fetchBillingNumber(billingNumber);
        navigate(
          { pathname: `/information/${qcuserid}`, search: location.search },
          { replace: true, state: { userData } },
        );
      } catch (err) {
        showBillingNumberError(err);
      }
    };

    showBillingInformation();
  }, [
    billingNumber,
    isLoading,
    isVerified,
    location.pathname,
    location.search,
    navigate,
    qcuserid,
  ]);

  // handling submit button
  const handleSubmit = async (e) => {
    e.preventDefault();
    const billNo = billNoRef.current.value;
    Swal.fire({
      title: "Is this correct?",
      text: `You've entered ${billNo}`,
      icon: "info",
      showCancelButton: true,
      scrollbarPadding: false,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes",
    }).then((result) => {
      if (result.isConfirmed) {
        fetchBillingNumber(billNo)
          .then((userData) => {
            Swal.fire({
              title: "Successful",
              icon: "success",
              showConfirmButton: false,
              allowOutsideClick: false,
              allowEscapeKey: false,
              heightAuto: false,
              timer: 3000,
              scrollbarPadding: false,
              didOpen: () => {
                Swal.showLoading();
              },
            }).then((swalResult) => {
              // if there are records found, navigate to information page
              if (swalResult.dismiss === Swal.DismissReason.timer) {
                navigate(`/information/${qcuserid}`, {
                  state: { userData },
                });
              }
            });
          })
          .catch(showBillingNumberError);
      }
    });
  };

  const handleModal = () => {
    setModal(!modal);
  };

  useEffect(() => {
    localStorage.clear();
  }, []);

  // // Check if the component is loaded inside an iframe
  // const isInIframe = window.self !== window.top;

  // // Render the component content only if it is inside an iframe
  // // to prevent manipulation of url parameters(eg. entering /:userid)
  // if (!isInIframe) {
  //   return null;
  // }

  // display content if in iframe
  let content;

  if (isLoading) {
    content = (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            border: "4px solid #f3f3f3",
            borderTop: "4px solid #3085d6",
            borderRadius: "50%",
            width: "50px",
            height: "50px",
            animation: "spin 1s linear infinite",
          }}
        />
        <p style={{ marginTop: "20px", fontSize: "18px", color: "#666" }}>
          Verifying access...
        </p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  } else if (!isVerified) {
    content = (
      <Error
        header="Access Denied"
        description="You don't have permission to view this site"
        btnTxt="RETURN"
      />
    );
  } else if (isVerified && billingNumber && location.state?.userData) {
    content = <Information />;
  } else {
    content = (
      <section className="landing">
        {modal && (
          <Modal
            isModalActive={modal}
            ariaLabel="payment-information-modal-title"
            ariaDescribe="payment-information-modal-description"
            imageUrl={SampleBillNo}
            imageAlt="Image of sample billing number"
            onCloseModal={(bool) => {
              setModal(bool);
            }}
          />
        )}

        <Row className="landing-card">
          <Col xl={6} xs={12}>
            <div className="landing-search-panel">
              <p className="landing-eyebrow">QC E-PAYMENT</p>
              <h1>Find your order of payment</h1>
              <p className="landing-description">
                Enter the billing number issued with your order of payment to
                review the details and continue securely.
              </p>
              <Form className="billing-search-form" onSubmit={handleSubmit}>
                <fieldset className="billing-search-fieldset">
                  <Form.Group>
                    <Form.Label htmlFor="billnoInput">
                      Billing number
                    </Form.Label>
                    <Form.Control
                      className="billnoInput"
                      id="billnoInput"
                      defaultValue={billingNumber || ""}
                      ref={billNoRef}
                      placeholder="e.g. M-2026-07-23-DBO-A248-000008"
                      autoComplete="off"
                      data-testid="billing-number-input"
                    />
                  </Form.Group>
                  <Button
                    className="billing-search-button"
                    variant="contained"
                    type="submit"
                    id="searchBtn"
                    disableElevation
                    data-testid="billing-search-button"
                  >
                    View payment details
                  </Button>
                  <p className="billing-search-note">
                    Your billing number is shown on your order of payment.
                  </p>
                </fieldset>
              </Form>
            </div>
          </Col>
          <Col xl={6} xs={12} className="my-auto">
            <div className="instructions-card">
              <div className="instructions-heading">
                <div>
                  <p className="landing-eyebrow">HOW IT WORKS</p>
                  <h2>Pay online in a few steps</h2>
                </div>
                <button
                  type="button"
                  className="instructions-info-button"
                  aria-label="View a sample billing number"
                  onClick={() => handleModal()}
                >
                  <InfoIcon sx={{ ...iconStyle, fontSize: 25 }} />
                </button>
              </div>
              <ol>
                <li>
                  Enter the billing number exactly as shown on your order of
                  payment.
                </li>
                {/*               <li>
                Click on the captcha box below it, beside the &quot;I&apos; m
                not a robot message&quot;.
              </li> */}
                <li>Review the payment details displayed for your order.</li>
                <li>
                  Select <strong>Pay online</strong> when you are ready to
                  proceed.
                </li>
                <li>
                  Choose your preferred available payment option to finish the
                  transaction.
                </li>
              </ol>
            </div>
            {/* <div className="instructionsDiv">
            <h3>Instructions for Building Permit On-Site Payment</h3>
            <ol>
              <li>Type in your Billing Number.</li>
              <li>
                Click on the captcha box below it, beside the &quot;I&apos;m not
                a robot message&quot;.
              </li>
              <li>Click on the &quot;Search&quot; button.</li>
              <li>
                If your Billing Number is in the system, your name will be
                displayed along with other details.
              </li>
              <li>
                After checking your account details, choose &quot;Pay
                On-Site&quot;.
              </li>
              <li>
                You will be instructed on how to pay your remaining balance
                manually.
              </li>
            </ol>
          </div> */}
          </Col>
        </Row>
        {backButton === "1" && (
          <button
            type="button"
            className="landing-back-button"
            onClick={() => navigate(-1)}
            data-testid="landing-back-button"
          >
            <ArrowBackIcon />
            Back to QCeServices
          </button>
        )}
      </section>
    );
  }

  return content;
}

export default Landing;
